import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Film, Pause, Play, RefreshCw, Square, Video } from 'lucide-react'
import Hls from 'hls.js'
import { jimiService, type JimiVideoFileRow } from '../services/jimiService'
import { useAuth } from '../contexts/AuthContext'

// =============================================================================
// AssetDashcamWindow — asset-scoped live video, snapshot, recorded clips,
// and recorded playback for a JIMI AI dashcam attached to an asset.
// =============================================================================
//
// Three tabs:
//   - Live      : start/stop live stream (HLS.js when supported, native
//                 <video> fallback). Shows the URL the vendor returns and
//                 a one-click capture button to drop a snapshot at the
//                 current frame (server-side; vendor decides storage).
//   - Snapshot  : on-demand still capture (separate from live).
//   - Recorded  : list of recorded clips with date filter; play/stop a
//                 selected clip via /video/playback/start.
//
// Operator/Admin role gating is delegated to fleet-api; the UI shows an
// inline "view-only" banner when the user lacks the required role.
// =============================================================================

export interface AssetDashcamWindowProps {
  assetId: string
  imei: string
  deviceName?: string | null
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

type Tab = 'live' | 'snapshot' | 'recorded'

const WRITE_ROLES = ['OPERATOR', 'ADMIN']

const AssetDashcamWindow: React.FC<AssetDashcamWindowProps> = ({
  assetId, imei, deviceName, showMessage,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const canWrite = WRITE_ROLES.includes(user?.role || '')

  const [tab, setTab] = useState<Tab>('live')
  const [liveUrl, setLiveUrl] = useState<string | null>(null)
  const [liveBusy, setLiveBusy] = useState(false)
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null)
  const [files, setFiles] = useState<JimiVideoFileRow[]>([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [filesFrom, setFilesFrom] = useState<string>('')
  const [filesTo, setFilesTo] = useState<string>('')
  const [playingFileId, setPlayingFileId] = useState<string | null>(null)
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef   = useRef<Hls | null>(null)
  const playbackVideoRef = useRef<HTMLVideoElement>(null)
  const playbackHlsRef   = useRef<Hls | null>(null)

  const notify = useCallback((msg: string, type: 'success' | 'error' | 'warning' | 'info') => {
    if (showMessage) showMessage(msg, type)
    else if (type === 'error') console.warn(msg)
  }, [showMessage])

  const attachHls = useCallback((url: string, ref: React.RefObject<HTMLVideoElement>, hlsHolder: React.MutableRefObject<Hls | null>) => {
    const el = ref.current
    if (!el) return
    if (hlsHolder.current) { hlsHolder.current.destroy(); hlsHolder.current = null }
    if (Hls.isSupported() && url.includes('.m3u8')) {
      const hls = new Hls()
      hls.loadSource(url)
      hls.attachMedia(el)
      hlsHolder.current = hls
    } else {
      el.src = url
    }
    el.play().catch(() => { /* autoplay blocked is fine */ })
  }, [])

  const stopLive = useCallback(async () => {
    if (!liveUrl) return
    setLiveBusy(true)
    try {
      await jimiService.videoLiveStop(imei)
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null }
      if (videoRef.current) { videoRef.current.pause(); videoRef.current.removeAttribute('src'); videoRef.current.load() }
      setLiveUrl(null)
      notify(t('asset_dashcam_live_stopped', 'Live stream stopped'), 'info')
    } catch (err: any) {
      notify(t('asset_dashcam_live_stop_error', 'Failed to stop live: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setLiveBusy(false) }
  }, [imei, liveUrl, notify, t])

  const startLive = useCallback(async () => {
    if (!canWrite) return
    setLiveBusy(true)
    try {
      const r = await jimiService.videoLiveStart(imei)
      const url = r?.url || (r as any)?.data?.url
      if (!url) throw new Error('vendor did not return a stream URL')
      setLiveUrl(url)
      setTimeout(() => attachHls(url, videoRef, hlsRef), 50)
      notify(t('asset_dashcam_live_started', 'Live stream started'), 'success')
    } catch (err: any) {
      notify(t('asset_dashcam_live_start_error', 'Failed to start live: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setLiveBusy(false) }
  }, [imei, canWrite, attachHls, notify, t])

  const captureSnapshot = useCallback(async () => {
    if (!canWrite) return
    try {
      const r = await jimiService.videoCapture(imei)
      const url = (r as any)?.url || (r as any)?.data?.url || (r as any)?.imageUrl
      if (url) setSnapshotUrl(url)
      notify(t('asset_dashcam_snapshot_ok', 'Snapshot requested'), 'success')
    } catch (err: any) {
      notify(t('asset_dashcam_snapshot_error', 'Snapshot failed: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    }
  }, [imei, canWrite, notify, t])

  const reloadFiles = useCallback(async () => {
    setFilesLoading(true)
    try {
      const r = await jimiService.videoFiles(imei, filesFrom || undefined, filesTo || undefined)
      const list = (r as any)?.data?.records || r?.records || []
      setFiles(list)
    } catch (err: any) {
      notify(t('asset_dashcam_files_load_error', 'Failed to list clips: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setFilesLoading(false) }
  }, [imei, filesFrom, filesTo, notify, t])

  const playFile = useCallback(async (file: JimiVideoFileRow) => {
    if (!canWrite) return
    setPlayingFileId(file.fileId)
    try {
      const r = await jimiService.videoPlaybackStart(imei, file.fileId)
      const url = (r as any)?.url || (r as any)?.data?.url
      if (!url) throw new Error('vendor did not return a playback URL')
      setPlaybackUrl(url)
      setTimeout(() => attachHls(url, playbackVideoRef, playbackHlsRef), 50)
    } catch (err: any) {
      setPlayingFileId(null)
      notify(t('asset_dashcam_playback_error', 'Playback failed: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    }
  }, [imei, canWrite, attachHls, notify, t])

  const stopPlayback = useCallback(async () => {
    if (!playingFileId) return
    try { await jimiService.videoPlaybackStop(imei) } catch { /* best-effort */ }
    if (playbackHlsRef.current) { playbackHlsRef.current.destroy(); playbackHlsRef.current = null }
    if (playbackVideoRef.current) { playbackVideoRef.current.pause(); playbackVideoRef.current.removeAttribute('src'); playbackVideoRef.current.load() }
    setPlayingFileId(null)
    setPlaybackUrl(null)
  }, [imei, playingFileId])

  // Load clips on tab switch.
  useEffect(() => { if (tab === 'recorded') reloadFiles() }, [tab, reloadFiles])

  // Cleanup on unmount: stop streams to release vendor resources.
  useEffect(() => () => {
    if (hlsRef.current) hlsRef.current.destroy()
    if (playbackHlsRef.current) playbackHlsRef.current.destroy()
    if (liveUrl) jimiService.videoLiveStop(imei).catch(() => {})
    if (playingFileId) jimiService.videoPlaybackStop(imei).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 flex items-center justify-center">
            <Camera className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-sm">{deviceName || t('asset_dashcam_default_name', 'AI Dashcam')}</div>
            <div className="text-xs text-gray-500 font-mono">IMEI {imei} · ASSET {assetId}</div>
          </div>
        </div>
        {!canWrite && (
          <div className="text-xs text-amber-600 dark:text-amber-400">
            {t('asset_dashcam_read_only', 'View only — operator role required for video controls.')}
          </div>
        )}
      </div>

      <div className="px-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
        {([
          { id: 'live',     label: t('asset_dashcam_tab_live', 'Live'),         icon: <Video className="w-4 h-4" /> },
          { id: 'snapshot', label: t('asset_dashcam_tab_snapshot', 'Snapshot'), icon: <Camera className="w-4 h-4" /> },
          { id: 'recorded', label: t('asset_dashcam_tab_recorded', 'Recorded'), icon: <Film className="w-4 h-4" /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setTab(it.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${
              tab === it.id
                ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {it.icon}{it.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'live' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {!liveUrl && (
                <button
                  type="button"
                  disabled={!canWrite || liveBusy}
                  onClick={startLive}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                >
                  <Play className="w-4 h-4" /> {t('asset_dashcam_start_live', 'Start live')}
                </button>
              )}
              {liveUrl && (
                <button
                  type="button"
                  disabled={liveBusy}
                  onClick={stopLive}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white"
                >
                  <Square className="w-4 h-4" /> {t('asset_dashcam_stop_live', 'Stop live')}
                </button>
              )}
              <button
                type="button"
                disabled={!canWrite}
                onClick={captureSnapshot}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera className="w-4 h-4" /> {t('asset_dashcam_capture', 'Capture')}
              </button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center">
              {liveUrl ? (
                <video ref={videoRef} className="w-full h-full" controls playsInline />
              ) : (
                <div className="text-gray-400 text-sm">
                  {t('asset_dashcam_live_idle', 'Live stream not started.')}
                </div>
              )}
            </div>
            {liveUrl && (
              <div className="text-xs text-gray-500 break-all">URL: {liveUrl}</div>
            )}
          </div>
        )}

        {tab === 'snapshot' && (
          <div className="space-y-3">
            <button
              type="button"
              disabled={!canWrite}
              onClick={captureSnapshot}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              <Camera className="w-4 h-4" /> {t('asset_dashcam_capture_now', 'Capture now')}
            </button>
            {snapshotUrl ? (
              <img src={snapshotUrl} alt="snapshot" className="rounded-lg border border-gray-200 dark:border-gray-700 max-h-[60vh]" />
            ) : (
              <div className="text-sm text-gray-500 italic">
                {t('asset_dashcam_snapshot_empty', 'No snapshot captured yet.')}
              </div>
            )}
          </div>
        )}

        {tab === 'recorded' && (
          <div className="space-y-3">
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('asset_dashcam_from', 'From')}</label>
                <input
                  type="datetime-local"
                  value={filesFrom}
                  onChange={(e) => setFilesFrom(e.target.value)}
                  className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('asset_dashcam_to', 'To')}</label>
                <input
                  type="datetime-local"
                  value={filesTo}
                  onChange={(e) => setFilesTo(e.target.value)}
                  className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
              </div>
              <button
                type="button"
                onClick={reloadFiles}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <RefreshCw className="w-4 h-4" /> {t('asset_dashcam_search', 'Search')}
              </button>
            </div>
            {filesLoading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
            {!filesLoading && files.length === 0 && (
              <div className="text-sm text-gray-500 italic">{t('asset_dashcam_files_empty', 'No clips found.')}</div>
            )}
            {!filesLoading && files.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {files.map((f) => {
                  const playing = playingFileId === f.fileId
                  return (
                    <div key={f.fileId} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-mono text-xs text-gray-500">{f.fileId}</div>
                          <div className="text-sm mt-1">
                            {f.beginTime ? new Date(f.beginTime).toLocaleString() : '—'}
                            {f.durationSec != null && ` · ${f.durationSec}s`}
                            {f.channel != null && ` · ch.${f.channel}`}
                          </div>
                        </div>
                        {!playing ? (
                          <button
                            type="button"
                            disabled={!canWrite}
                            onClick={() => playFile(f)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                          >
                            <Play className="w-3.5 h-3.5" /> {t('asset_dashcam_play', 'Play')}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={stopPlayback}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Pause className="w-3.5 h-3.5" /> {t('asset_dashcam_stop', 'Stop')}
                          </button>
                        )}
                      </div>
                      {playing && playbackUrl && (
                        <div className="aspect-video bg-black rounded-md overflow-hidden mt-2">
                          <video ref={playbackVideoRef} className="w-full h-full" controls playsInline />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetDashcamWindow
